import CommChannelFactory from './CommChannelFactory';

// Message types for viewer presence events
const viewerJoinedMessageType = 'viewer.joined';
const viewerLeftMessageType = 'viewer.left';

/**
 * Base class for Agora adapters with shared functionality.
 */
export default class AgoraAdapterBase {
  constructor() {
    this.client = null;
    this.uid = null;
    this.token = null;
    this.appId = null;
    this.roomName = null;
    this.serverData = null;
    this.videoElId = null;
    this.options = {};
    this.debug = false;
    this.muted = false;

    // Initialize comm channel but don't connect yet (need appId, roomName first).
    this.commChannel = null;
  }

  /**
   * Initialize common components of the adapter.
   * @param {Object} options - Configuration options.
   */
  async initCommon(options) {
    this.options = options || {};
    this.debug = options.debug || false;
    this.log('Initializing Agora adapter base');
    this.streamId = options.streamId || null;

    // Store reference to video element ID.
    this.videoElId = options.videoElId;

    // Set common callback handlers.
    this.onStreamEnd = options.onStreamEnd || (() => {});
    this.onStreamError = options.onStreamError || (() => {});
    this.onCustomDataGet = options.onCustomDataGet || (() => {});
    this.showErrorMessage = options.showErrorMessage;
    this.showInfoMessage = options.showInfoMessage;
    this.roomName = options.roomName;
    this.onViewerCountChanged = options.onViewerCountChanged || (() => {});

    // Initialize comm channel with callback for received messages.
    if (options.commChannel) {
      this.commChannel = options.commChannel;
    } else {
      // Create comm channel using factory with the specified type or default.
      const chatType = options.chatType || 'pusher';
      this.commChannel = await CommChannelFactory.create(
        chatType,
        {
          debug: this.debug,
          onMessageReceived: (message) => this._handleCustomMessage(message),
          onMemberJoined: (member) => this._handleMemberJoined(member),
          onMemberLeft: (member) => this._handleMemberLeft(member),
          store: options.store, // Pass store for Pusher client.
          streamId: this.streamId,
        },
      );
    }
  }

  /**
   * Setup event listeners for connection state changes.
   */
  setupClientListeners() {
    if (!this.client) return;

    this.client.on('error', (err) => {
      this.error('Client error:', err);
      this.onStreamError(
        err.message || 'Stream connection error',
      );
    });
  }

  /**
   * Set configuration received from server.
   * @param {String} key - Config key.
   * @param {Object} value - Config value.
   */
  setConfig(key, value) {
    if (key === 'serverData') {
      this.serverData = value;

      if (value?.streamId) {
        this.streamId = value.streamId;
      }

      try {
        // Extract credentials from server data.
        if (value.appId) {
          // If using direct format.
          this.appId = value.appId;
          this.roomName = value.roomName || value.channel;
          this.token = value.token;
          this.uid = value.uid || null;
        } else if (value.Token && value.Message) {
          // If using the StreamModule format - this format is used by the
          // existing system.
          this.token = value.Token;

          if (value.Message) {
            this.appId = value.Message.AppID;
            this.roomName = value.Message.ChannelName;
            this.uid = value.Message.ClientUID || null;
          }
        } else {
          throw new Error('Invalid server data format');
        }

        // Initialize comm channel with updated configuration.
        if (this.commChannel) {
          this.commChannel.initialize({
            appId: this.appId,
            streamId: this.streamId,
            uid: this.uid,
          });
        }
      } catch (error) {
        this.error('Error processing server data:', error);
        if (this.onServerDataValidateError) {
          this.onServerDataValidateError('Invalid server data format');
        } else {
          this.onStreamError('Invalid server data format');
        }
      }
    }
  }

  async connectChat() {
    try {
      if (this.commChannel) {
        await this.commChannel.connect(this.streamId);
      }
    } catch (error) {
      this.error('Failed to connect to chat:', error);
    }
  }

  async toggleCommChannelSubscription(connected, streamId = null) {
    this.commChannel?.toggleSubscription(connected, streamId);
  }

  /**
   * Process incoming custom messages.
   * @param {Object} message - The received message.
   */
  _handleCustomMessage(message) {
    if (message && message.data) {
      this.onCustomDataGet(message.data);
    }
  }

  _handleMemberJoined(member) {
    // Translate this for legacy expectations,
    // which emits everything chat related through the onCustomDataGet.
    // The join message expects avatar and username.

    // Some code expects this data to be nested under `data`
    // and some code expects it to be at the top level.
    // When it's nested, `type` exists in both.
    const messageData = {
      type: viewerJoinedMessageType,
      username: member.info.name,
      avatar: member.info.avatar,
      viewerToken: member.id,
    };
    this.onCustomDataGet(messageData);

    if (this.onViewerCountChanged) {
      const count = this.commChannel.getMemberCount();
      this.onViewerCountChanged(count);
    }
  }

  _handleMemberLeft(member) {
    // Translate this for legacy expectations,
    // which emits everything chat related through the onCustomDataGet.
    // The leave message expects avatar and username.
    const messageData = {
      type: viewerLeftMessageType,
      viewerToken: member.id,
    };
    this.onCustomDataGet(messageData);

    if (this.onViewerCountChanged) {
      const count = this.commChannel.getMemberCount();
      this.onViewerCountChanged(count);
    }
  }

  /**
   * Send a custom message through RTM.
   * @param {Object} message - Message to send.
   */
  async sendCustomMessage(message) {
    try {
      if (this.commChannel && this.commChannel.isConnected()) {
        await this.commChannel.sendMessage(message);
        return true;
      }
      this.error('comm channel not connected');
      return false;
    } catch (error) {
      this.error('Failed to send message:', error);
      return false;
    }
  }

  /**
   * Clean up Chat resources.
   */
  async cleanupCommChannel() {
    if (this.commChannel) {
      await this.commChannel.disconnect();
    }
  }

  /**
   * Log a debug message.
   */
  log(...args) {
    if (this.debug) {
      // eslint-disable-next-line no-console
      console.log('[AgoraAdapter]', ...args);
    }
  }

  /**
   * Log an error message.
   */
  error(...args) {
    if (this.debug) {
      // eslint-disable-next-line no-console
      console.error('[AgoraAdapter]', ...args);
    }
  }
}
